import type { GetApiAdminCompaniesResponse } from '@sit/client-shared';
import {
  sortByBoolean,
  sortByDateString,
  sortByNumber,
  sortByString,
} from '@sit/client-shared';
import { type TenantType, TenantTypes } from '@sit/core';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableCheckbox,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
  Sort,
} from 'carbon-react/lib/components/flat-table';
import Icon from 'carbon-react/lib/components/icon';
import Pager from 'carbon-react/lib/components/pager';
import Search, { type SearchEvent } from 'carbon-react/lib/components/search';
import { MultiSelect, Option } from 'carbon-react/lib/components/select';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toCarbonSortOrder } from '../../helpers/carbon';
import { createSumoLogicQueryLinkForCompany } from '../../helpers/sumologic';
import { BulkActions } from './BulkActions';
import { SyncTriggersPackageButton } from './SyncTriggersPackageButton';

const PAGE_SIZES = Object.freeze(
  [10, 20, 50, 100].map((v) => ({ id: `${v}`, name: v })),
);

export interface CompaniesTableProps {
  companies: GetApiAdminCompaniesResponse;
}

const headDataItems = [
  { id: 'selected', name: '', sortable: false },
  { id: 'externalId', name: 'Company ID' },
  { id: 'id', name: 'SIT ID' },
  { id: 'tenantType', name: 'Tenant Type' },
  { id: 'entities', name: 'Entities', sortable: false },
  { id: 'dataSyncActive', name: 'Triggers' },
  { id: 'lastLoginAttempt', name: 'Last Login Attempt' },
  { id: 'isImplemented', name: 'Implemented' },
  { id: 'disableEntitySlideIn', name: 'Disable Entity Slide In' },
  { id: 'isProvisioned', name: 'Provisioned' },
  { id: 'adminUsername', name: 'Admin Username' },
  { id: 'adminEmail', name: 'Admin Email' },
  { id: 'managedAtEntityLevel', name: 'Entity Level' },
  { id: 'syncStart', name: 'Sync Start' },
  { id: 'syncEnd', name: 'Sync End' },
  { id: 'syncError', name: 'Sync Error' },
  { id: 'triggerPackageVersion', name: 'Trigger package' },
] satisfies {
  id: HeaderItemId | 'selected';
  sortable?: boolean;
  name: string;
}[];

type HeaderItemId = keyof GetApiAdminCompaniesResponse[number];

const tenantTypeOptions = Object.values(TenantTypes).map((type) => ({
  id: type,
  name: type,
}));

export function CompaniesTable({ companies }: CompaniesTableProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>('asc');
  const [recordsRange, setRecordsRange] = useState(() => ({
    start: 0,
    end: 5,
  }));
  const [sortedColumn, setSortedColumn] = useState<HeaderItemId>('externalId');
  const [checkedRows, setCheckedRows] = useState<Record<number, boolean>>({});
  const [tenantTypeFilter, setTenantTypeFilter] = useState<TenantType[]>([]);

  const handlePagination = (newPage: number, newPageSize: number) => {
    const adjustment = currentPage === newPage ? 0 : newPage - currentPage;
    let start =
      adjustment > 0
        ? recordsRange.start + newPageSize
        : recordsRange.start - newPageSize;
    if (newPageSize !== pageSize) {
      start =
        start < 0 || Math.abs(start - recordsRange.end) > newPageSize
          ? 0
          : start;
    }
    if (Math.abs(adjustment) > 1) {
      if (adjustment < 0)
        start = Math.max(start + 10 + adjustment * newPageSize, 0);
      else start = start - 10 + adjustment * newPageSize;
    }
    setRecordsRange({ start, end: start + newPageSize });
    setCurrentPage(newPage);
    setPageSize(newPageSize);
  };

  const handleSortClick = (value: HeaderItemId) => {
    setSortedColumn(value);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const sortData = useCallback(
    (dataToSort: GetApiAdminCompaniesResponse, columnToSort: HeaderItemId) => {
      if (dataToSort.length === 0) return dataToSort;
      let sortedData = [...dataToSort];
      if (
        columnToSort === 'lastLoginAttempt' ||
        columnToSort === 'reactivatedOn' ||
        columnToSort === 'syncEnd' ||
        columnToSort === 'syncStart' ||
        columnToSort === 'archivedOn'
      ) {
        sortedData = sortByDateString(dataToSort, columnToSort, sortOrder);
      }
      const firstNonNullValue = dataToSort.find(
        (item) => item[columnToSort] != null,
      )?.[columnToSort];
      if (typeof firstNonNullValue === 'string') {
        sortedData = sortByString(dataToSort, columnToSort, sortOrder);
      }
      if (typeof firstNonNullValue === 'number') {
        sortedData = sortByNumber(dataToSort, columnToSort, sortOrder);
      }
      if (typeof firstNonNullValue === 'boolean') {
        sortedData = sortByBoolean(dataToSort, columnToSort, sortOrder);
      }
      return sortedData;
    },
    [sortOrder],
  );

  const onHighlight = (
    e: React.MouseEvent<HTMLElement>,
    id: number | string,
  ) => {
    navigate(`/companies/${id}`);
  };

  const { data: filteredData, totalRecords } = useMemo(() => {
    let data = companies;
    if (search) {
      const searchLower = search.toLowerCase();
      const searchNumber = Number.parseInt(search, 10);
      const shouldSearchNumber = !Number.isNaN(searchNumber);
      const searchFilter = shouldSearchNumber
        ? (company: GetApiAdminCompaniesResponse[number]) =>
            company.externalId.toLowerCase().includes(searchLower) ||
            `${company.id}`.includes(search)
        : (company: GetApiAdminCompaniesResponse[number]) =>
            company.externalId.toLowerCase().includes(searchLower);
      data = data.filter((company) => searchFilter(company));
    }
    if (tenantTypeFilter.length > 0) {
      data = data.filter(
        (company) =>
          company.tenantType && tenantTypeFilter.includes(company.tenantType),
      );
    }
    return {
      data,
      totalRecords: data.length,
    };
  }, [search, tenantTypeFilter, companies]);

  const sortedData = useMemo(
    () => sortData(filteredData, sortedColumn),
    [sortData, filteredData, sortedColumn],
  );
  const pageData = sortedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const selectedCompanies = useMemo(
    () => companies.filter((company) => checkedRows[company.id]),
    [companies, checkedRows],
  );

  const onChangeSearch = (ev: SearchEvent) => {
    setSearch(ev.target.value);
    setCurrentPage(1);
  };

  const onTenantTypeFilterChange = (
    ev: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTenantTypeFilter(ev.target.value as unknown as TenantType[]);
    setCurrentPage(1);
  };

  return (
    <Box width="100%">
      <Box display="flex" width="100%" mb={2}>
        <Box flexGrow={1} mr={2}>
          <Search
            onChange={onChangeSearch}
            value={search}
            searchButton
            placeholder="Search Company ID or SIT ID"
          />
        </Box>
        <Box flexGrow={0} minWidth={400}>
          <MultiSelect
            placeholder="Filter by tenant types"
            labelInline
            value={tenantTypeFilter}
            onChange={onTenantTypeFilterChange}
          >
            {tenantTypeOptions.map((option) => (
              <Option key={option.id} value={option.id} text={option.name} />
            ))}
          </MultiSelect>
        </Box>
        <Box>
          <Link to="/provision">
            <Button>Provision company</Button>
          </Link>
        </Box>
      </Box>
      <Box width="100%">
        <FlatTable
          mt={2}
          colorTheme="transparent-white"
          hasStickyFooter
          hasStickyHead
          overflowX="auto"
          width="100%"
          footer={
            <Pager
              totalRecords={totalRecords}
              showPageSizeSelection
              pageSize={pageSize}
              currentPage={currentPage}
              onPagination={handlePagination}
              // @ts-expect-error -- OK that it's not mutable
              pageSizeSelectionOptions={PAGE_SIZES}
            />
          }
        >
          <FlatTableHead>
            <FlatTableRow>
              {headDataItems.map((dataItem) => (
                <FlatTableHeader key={dataItem.id}>
                  {dataItem.sortable === false ? (
                    dataItem.name
                  ) : (
                    <Sort
                      onClick={() => handleSortClick(dataItem.id)}
                      sortType={
                        dataItem.id === sortedColumn
                          ? toCarbonSortOrder(sortOrder)
                          : undefined
                      }
                    >
                      {dataItem.name}
                    </Sort>
                  )}
                </FlatTableHeader>
              ))}
              <FlatTableHeader>Actions</FlatTableHeader>
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>
            {pageData.map((company) => (
              <FlatTableRow
                key={company.id}
                onClick={(e) => onHighlight(e, company.id)}
              >
                <FlatTableCheckbox
                  onChange={(e) => {
                    e.stopPropagation();
                    setCheckedRows((rows) => ({
                      ...rows,
                      [company.id]: !rows[company.id],
                    }));
                  }}
                  checked={!!checkedRows[company.id]}
                />
                <FlatTableCell>{company.externalId}</FlatTableCell>
                <FlatTableCell>{company.id}</FlatTableCell>
                <FlatTableCell>{company.tenantType}</FlatTableCell>
                <FlatTableCell truncate width={300}>
                  {company.entities.map((e) => e.name).join(', ')}
                </FlatTableCell>
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={company.dataSyncActive}
                />
                <FlatTableCell>{company.lastLoginAttempt}</FlatTableCell>
                {/** Empty onChange to avoid "You provided a `checked` prop to a form field without an `onChange` handler. This will render a read-only field. If the field should be mutable use `defaultChecked`. Otherwise, set either `onChange` or `readOnly`." */}
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={company.isImplemented}
                />
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={company.disableEntitySlideIn}
                />
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={company.isProvisioned}
                />
                <FlatTableCell>{company.adminUsername}</FlatTableCell>
                <FlatTableCell>{company.adminEmail}</FlatTableCell>
                <FlatTableCheckbox
                  onChange={() => void 0}
                  checked={company.managedAtEntityLevel}
                />
                <FlatTableCell>{company.syncStart}</FlatTableCell>
                <FlatTableCell>{company.syncEnd}</FlatTableCell>
                <FlatTableCell>{company.syncError}</FlatTableCell>
                <FlatTableCell>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {company.triggerPackageVersion ?? '-'}
                    <SyncTriggersPackageButton
                      companyTreeSettingsId={company.id}
                      externalId={company.externalId}
                    />
                  </Box>
                </FlatTableCell>
                <FlatTableCell>
                  <a
                    href={createSumoLogicQueryLinkForCompany(
                      company.externalId,
                    )}
                    target="_blank"
                    title={`View SumoLogic logs for ${company.externalId}`}
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Icon type="search" />
                  </a>
                </FlatTableCell>
              </FlatTableRow>
            ))}
          </FlatTableBody>
        </FlatTable>
      </Box>
      <BulkActions selectedCompanies={selectedCompanies} />
    </Box>
  );
}
